// @ts-nocheck
import { Component } from '@theme/component';
import {
  fetchStoreDetails,
  formatPhoneHref,
  formatOpeningHours,
  getStoreHoursStatus,
  getDirectionsUrl,
  getMainPhotoUrl,
  getPhotoByType,
} from '@theme/store-locator-helpers';

/** Fallback appointment page, used only if the section didn't pass a localized URL */
const APPOINTMENT_PAGE_URL = '/pages/book-an-appointment';

/** Query params read by the booking widget to preselect a store (see tulip-appointments.js) */
const APPOINTMENT_STORE_PARAM = 'store';
const APPOINTMENT_STORE_CITY_PARAM = 'store_city';
const ESCAPE_HTML_PATTERN = /[&<>]/g;
const ESCAPE_HTML_REPLACEMENTS = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
};

/**
 * Store Details Component
 * Fetches and displays detailed information for a single store location
 */
class StoreDetails extends Component {
  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {Object|null} */
  #store = null;

  async connectedCallback() {
    super.connectedCallback();

    const storeId = this.dataset.storeId;
    const apiKey = this.dataset.apiKey;

    if (!storeId || !apiKey) {
      this.#renderError();
      return;
    }

    await this.#fetchAndRender(apiKey, storeId);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * Get translations from data attributes
   * @returns {Object} Translation strings
   */
  #getTranslations() {
    return {
      open_now: this.dataset.tOpenNow || 'Open Now',
      open_until: this.dataset.tOpenUntil || 'Open until {{ time }}',
      opens_at: this.dataset.tOpensAt || 'Opens at {{ time }}',
      closed: this.dataset.tClosed || 'Closed',
      temporarily_closed: this.dataset.tTemporarilyClosed || 'Temporarily Closed',
      coming_soon: this.dataset.tComingSoon || 'Opening {{ date }}',
      get_directions: this.dataset.tGetDirections || 'Get Directions',
      call: this.dataset.tCall || 'Call',
      book_appointment: this.dataset.tBookAppointment || 'Book an appointment',
      services_hours: this.dataset.tServicesHours || 'Hours & Services',
      services: this.dataset.tServices || 'Services',
      hours: this.dataset.tHours || 'Hours',
      events: this.dataset.tEvents || 'Events',
      no_events: this.dataset.tNoEvents || 'No events planned.',
    };
  }

  /**
   * Get day names from data attribute
   * @returns {string[]} Array of day names (index 0 empty, 1-7 are Mon-Sun)
   */
  #getDayNames() {
    const daysStr = this.dataset.tDays;
    if (!daysStr) {
      return ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    }
    return ['', ...daysStr.split(',')];
  }

  /**
   * Fetch store data and render
   * @param {string} apiKey - Uberall store finder key
   * @param {string} storeId - Store location ID
   */
  async #fetchAndRender(apiKey, storeId) {
    this.#abortController = new AbortController();

    try {
      const store = await fetchStoreDetails(apiKey, storeId, this.#abortController.signal);

      if (!store) {
        this.#renderError();
        return;
      }

      this.#store = store;
      this.#render();
      this.#updateStoreImage();
      this.#updateHeroImage();
      this.#updateAboutContent();
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.#renderError();
      }
    }
  }

  /**
   * Get custom field value from store
   * @param {string} fieldKey - The custom field key to look for
   * @returns {string|null} - The field value or null
   */
  #getCustomField(fieldKey) {
    const store = this.#store;
    if (!store?.customFields || !Array.isArray(store.customFields)) return null;
    const field = store.customFields.find((f) => f.key === fieldKey || f.name === fieldKey);
    return field?.value || field?.text || null;
  }

  /** Update the About Store block content. */
  #updateAboutContent() {
    const store = this.#store;
    if (!store) return;

    // Try to get About_store from customFields first, then from direct property
    const aboutText = this.#getCustomField('About_store') || store.About_store;
    if (!aboutText) return;

    // Find the about container in the parent section
    const section = this.closest('.store-details-section-wrapper');
    if (!section) return;

    const aboutContainer = section.querySelector('[data-store-about]');
    if (!aboutContainer) return;

    // Set the content (preserve HTML if it contains formatting)
    aboutContainer.innerHTML = `<p>${this.#escapeHtml(aboutText)}</p>`;
  }

  /** Update the hero block with the STOREFINDER_COVER photo from the API. */
  #updateHeroImage() {
    const store = this.#store;
    if (!store?.photos) return;

    const coverPhotoUrl = getPhotoByType(store.photos, 'STOREFINDER_COVER');
    if (!coverPhotoUrl) return;

    // Find the hero container in the parent section wrapper
    const sectionWrapper = this.closest('.store-details-section-wrapper');
    if (!sectionWrapper) return;

    const heroContainer = sectionWrapper.querySelector('[data-store-hero]');
    if (!heroContainer) return;

    // Create new image element
    const img = document.createElement('img');
    img.src = coverPhotoUrl;
    img.alt = this.#cleanStoreName(store.name);
    img.className = 'store-hero-block__img';
    img.loading = 'eager';
    img.fetchPriority = 'high';

    // Replace shimmer with image
    heroContainer.innerHTML = '';
    heroContainer.appendChild(img);
  }

  /** Update the store image with the main photo from the API. */
  #updateStoreImage() {
    const store = this.#store;
    if (!store?.photos) return;

    const mainPhotoUrl = getMainPhotoUrl(store.photos);
    if (!mainPhotoUrl) return;

    // Find the image container in the parent section
    const section = this.closest('.store-details-section');
    if (!section) return;

    const imageContainer = section.querySelector('[data-store-image]');
    if (!imageContainer) return;

    const mainPhoto = store.photos.find((photo) => photo.type === 'MAIN');

    // Create new image element with high priority (above the fold)
    const img = document.createElement('img');
    img.src = mainPhotoUrl;
    img.alt =
      mainPhoto?.description ||
      `Veronica Beard ${this.#cleanStoreName(store.name)} physical store front`;
    img.className = 'store-details-section__img';
    img.loading = 'eager';
    img.fetchPriority = 'high';

    // Replace existing content
    imageContainer.innerHTML = '';
    imageContainer.appendChild(img);
  }

  /** Render error state. */
  #renderError() {
    this.innerHTML = `
      <div class="store-details__error">
        <p>Unable to load store information.</p>
      </div>
    `;
  }

  /**
   * Clean store name by removing brand prefix
   * @param {string} name - Raw store name
   * @returns {string} Cleaned store name
   */
  #cleanStoreName(name) {
    if (!name) return '';
    // Remove "Veronica Beard " prefix and any "Brand - " style prefixes
    return (
      name
        .replace(/^Veronica Beard\s*/i, '')
        .replace(/^[^-]+-\s*/, '')
        .trim() || name
    );
  }

  /**
   * Whether this store's Uberall ID is in the theme's stores-with-appointments list
   * @returns {boolean}
   */
  #hasAppointments() {
    const storeId = (this.dataset.storeId || '').trim();
    if (!storeId) return false;

    return (this.dataset.appointmentStoreIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
      .includes(storeId);
  }

  /**
   * Appointment page URL carrying this store as a hint so the booking widget can preselect it
   * @returns {string} Path with query string
   */
  #buildAppointmentUrl() {
    const url = new URL(
      this.dataset.appointmentUrl || APPOINTMENT_PAGE_URL,
      window.location.origin,
    );
    const storeName = this.#cleanStoreName(this.#store?.name);
    const storeCity = this.#store?.city;

    if (storeName) url.searchParams.set(APPOINTMENT_STORE_PARAM, storeName);
    if (storeCity) url.searchParams.set(APPOINTMENT_STORE_CITY_PARAM, storeCity);

    return url.pathname + url.search;
  }

  /**
   * Render the store details
   */
  #render() {
    const store = this.#store;
    if (!store) return;

    const translations = this.#getTranslations();
    const dayNames = this.#getDayNames();
    const status = getStoreHoursStatus(store, translations);
    const formattedHours = formatOpeningHours(store.openingHours, dayNames);
    const directionsUrl = getDirectionsUrl(store);
    const cleanName = this.#cleanStoreName(store.name);
    const appointmentUrl = this.#hasAppointments()
      ? this.#buildAppointmentUrl()
      : `tel:${formatPhoneHref(store.phone)}`;

    this.innerHTML = `
      <div class="store-details__header">
        <h1 class="store-details__name h1">${this.#escapeHtml(cleanName)}</h1>
        <div class="store-details__status ${status.isOpen ? 'is-open' : ''}">
          <span class="store-details__status-dot"></span>
          <span class="store-details__status-text">${status.text}</span>
        </div>
      </div>

      <address class="store-details__address">
        ${store.streetAndNumber ? `<span class="store-details__address-line">${this.#escapeHtml(store.streetAndNumber)}</span>` : ''}
        <span class="store-details__address-line">
          ${this.#escapeHtml([store.city, store.province, store.zip].filter(Boolean).join(', '))}
        </span>
        ${store.phone ? `<a href="tel:${formatPhoneHref(store.phone)}" class="store-details__phone">${this.#escapeHtml(store.phone)}</a>` : ''}
      </address>

      ${this.#renderHours(formattedHours, translations)}

      <div class="store-details__actions">
        <a href="${directionsUrl}" target="_blank" rel="noopener" class="store-details__action-link button button-primary">
          ${translations.get_directions}
        </a>
        <a href="${appointmentUrl}" class="store-details__action-link button button-secondary">
          ${translations.book_appointment}
        </a>
      </div>

      ${this.#renderServicesAccordion(store.services, translations)}
      ${this.#renderEventsAccordion(translations)}
    `;
  }

  /**
   * Render store hours
   * @param {string[]} formattedHours - Formatted opening hours
   * @param {Object} translations - Translation strings
   * @returns {string} HTML string
   */
  #renderHours(formattedHours, translations) {
    if (!formattedHours || formattedHours.length === 0) return '';

    return `
      <h3 class="store-details__hours-title h3">${translations.hours}</h3>
      <div class="store-details__hours">
        ${formattedHours.map((line) => `<span class="store-details__hours-line">${this.#escapeHtml(line)}</span>`).join('')}
      </div>
    `;
  }

  /**
   * Render services accordion with plus/minus icons
   * @param {string[]} services - Available services
   * @param {Object} translations - Translation strings
   * @returns {string} HTML string
   */
  #renderServicesAccordion(services, translations) {
    if (!services || services.length === 0) return '';

    const iconPlus = this.dataset.iconPlus || '';
    const iconMinus = this.dataset.iconMinus || '';

    return `
      <details class="store-details__accordion" open>
        <summary class="store-details__accordion-header h2">
          <span>${translations.services}</span>
          <span class="store-details__accordion-icon">
            <span class="icon-plus svg-wrapper">${iconPlus}</span>
            <span class="icon-minus svg-wrapper">${iconMinus}</span>
          </span>
        </summary>
        <div class="store-details__accordion-content">
          <ul class="store-details__services-list">
            ${services.map((service) => `<li>${this.#escapeHtml(service)}</li>`).join('')}
          </ul>
        </div>
      </details>
    `;
  }

  /**
   * Render events accordion with plus/minus icons
   * @param {Object} translations - Translation strings
   * @returns {string} HTML string
   */
  #renderEventsAccordion(translations) {
    const iconPlus = this.dataset.iconPlus || '';
    const iconMinus = this.dataset.iconMinus || '';

    // Get events from customFields or direct property
    const eventsCustom = this.#getCustomField('events_custom') || this.#store?.events_custom;

    // Parse events if it's a string (could be comma-separated or JSON)
    let events = [];
    if (eventsCustom) {
      if (typeof eventsCustom === 'string') {
        // Try parsing as JSON first, otherwise treat as single event or comma-separated
        try {
          events = JSON.parse(eventsCustom);
          if (!Array.isArray(events)) events = [events];
        } catch {
          // Treat as comma-separated or single event
          events = eventsCustom
            .split(',')
            .map((e) => e.trim())
            .filter(Boolean);
        }
      } else if (Array.isArray(eventsCustom)) {
        events = eventsCustom;
      }
    }

    const hasEvents = events.length > 0;
    const content = hasEvents
      ? `<ul class="store-details__services-list">
          ${events.map((event) => `<li>${this.#escapeHtml(typeof event === 'object' ? event.name || event.title || JSON.stringify(event) : event)}</li>`).join('')}
        </ul>`
      : `<p class="store-details__no-events">${translations.no_events}</p>`;

    return `
      <details class="store-details__accordion">
        <summary class="store-details__accordion-header h2">
          <span>${translations.events}</span>
          <span class="store-details__accordion-icon">
            <span class="icon-plus svg-wrapper">${iconPlus}</span>
            <span class="icon-minus svg-wrapper">${iconMinus}</span>
          </span>
        </summary>
        <div class="store-details__accordion-content">
          ${content}
        </div>
      </details>
    `;
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  #escapeHtml(str) {
    if (!str) return '';
    return String(str).replace(
      ESCAPE_HTML_PATTERN,
      (char) => ESCAPE_HTML_REPLACEMENTS[char] || char,
    );
  }
}

if (!customElements.get('store-details')) {
  customElements.define('store-details', StoreDetails);
}

export default StoreDetails;
